// 2D canvas graphics for the Events page: the example bracket (the dome plan is shared/plan.ts).
// Drawn on demand only (one frame per state change, never a loop). DPR capped at 2.

const css = (name: string, el: Element = document.documentElement) => getComputedStyle(el).getPropertyValue(name).trim();

function setupCanvas(cv: HTMLCanvasElement, vw: number, vh: number, draw: (ctx: CanvasRenderingContext2D, px: number) => void) {
  const ctx = cv.getContext("2d")!;
  let scale = 1;
  let queued = false;
  const resize = () => {
    const dpr = Math.min(2, devicePixelRatio || 1);
    const w = cv.clientWidth || vw;
    scale = w / vw;
    cv.width = Math.round(w * dpr);
    cv.height = Math.round((w * vh / vw) * dpr);
    ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    paint();
  };
  const paint = () => { ctx.clearRect(0, 0, vw, vh); draw(ctx, 1 / scale); };
  const request = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; paint(); });
  };
  new ResizeObserver(resize).observe(cv);
  resize();
  return { request, paint };
}

/* ---------- Bracket: 8 pairs, quarter finals to a champion ---------- */
export type BracketState = { lines: number[]; labels: number[] };

export function createBracket(cv: HTMLCanvasElement, rounds: string[][], narrow: boolean, state: BracketState) {
  const PW = narrow ? 48 : 92, PH = 32, COL = narrow ? 80 : 132;
  const VW = 3 * COL + PW + 2, VH = 336;
  cv.style.aspectRatio = `${VW} / ${VH}`;
  const ys: number[][] = [rounds[0].map((_, i) => 21 + i * 42)];
  for (let r = 1; r < rounds.length; r++) ys[r] = rounds[r].map((_, i) => (ys[r - 1][2 * i] + ys[r - 1][2 * i + 1]) / 2);
  const c = { layer1: css("--layer-1", cv), borderStrong: css("--border-strong", cv), ink: css("--ink", cv), court: css("--accent", cv), fill: css("--accent-fill", cv), wash: css("--court-wash", cv), font: css("--font-text", cv) };

  const g = setupCanvas(cv, VW, VH, (ctx, px) => {
    // connectors
    ctx.strokeStyle = c.court; ctx.lineWidth = 1.5 * px; ctx.lineCap = "butt";
    for (let r = 0; r < rounds.length - 1; r++) {
      const p = state.lines[r] ?? 0;
      if (p <= 0) continue;
      for (let m = 0; m < rounds[r].length / 2; m++) {
        const xa = r * COL + PW + 1, xm = xa + 20, xn = (r + 1) * COL;
        const ya = ys[r][2 * m], yb = ys[r][2 * m + 1], ymid = (ya + yb) / 2;
        const l1 = 2 * (xm - xa) + (yb - ya), l2 = xn - xm, total = l1 + l2;
        const d = p * total;
        ctx.setLineDash([Math.min(d, l1), l1 + 1]);
        ctx.beginPath(); ctx.moveTo(xa, ya); ctx.lineTo(xm, ya); ctx.lineTo(xm, yb); ctx.lineTo(xa, yb); ctx.stroke();
        if (d > l1) {
          ctx.setLineDash([d - l1, l2 + 1]);
          ctx.beginPath(); ctx.moveTo(xm, ymid); ctx.lineTo(xn, ymid); ctx.stroke();
        }
      }
    }
    ctx.setLineDash([]);
    // pills
    ctx.font = `600 16px ${c.font}`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    rounds.forEach((names, r) => {
      const a = state.labels[r] ?? 0;
      if (a <= 0) return;
      ctx.globalAlpha = a;
      const champ = r === rounds.length - 1;
      names.forEach((name, i) => {
        const x = r * COL + 1, y = ys[r][i];
        ctx.fillStyle = champ ? c.fill : c.layer1;
        ctx.beginPath(); ctx.roundRect(x, y - PH / 2, PW, PH, 10); ctx.fill();
        ctx.fillStyle = c.ink;
        ctx.fillText(narrow ? name.replace("Pair ", "") : name, x + PW / 2, y + 1);
      });
      ctx.globalAlpha = 1;
    });
  });
  return g.request;
}
